import type { BackCushionDesignType } from '../schemas/banquette-back-cushion-template.schema';
import type { UpholsteryDesignAllowances } from '../schemas/upholstery-design-settings.schema';
import { resolveDesignTraits, type UpholsteryDesignTraits } from './upholstery-design-traits';

/**
 * Resolves the per-design-type fabric design allowance percentage.
 *
 * Returns null when no field is explicitly set (caller falls through to material default).
 * Returns a number — including 0 — when at least one field is set, meaning the user has
 * explicitly configured this design type.
 *
 * `per_channel_pct` applies to a **tufted** design, not merely one with
 * channels: `channel_seam` counts channels but has never scaled its allowance
 * by them. `per_button_pct` applies to a design with buttons.
 *
 * `traits` is optional; without it the seeded map answers, which reproduces the
 * string-literal comparisons this used to do for the eight shipped designs.
 */
export function resolveDesignTypeAllowancePct(
  designType: BackCushionDesignType,
  allowances: UpholsteryDesignAllowances | null | undefined,
  channelsPerSection: number,
  buttonsPerSection: number,
  traits?: UpholsteryDesignTraits,
): number | null {
  const entry = allowances?.[designType];
  if (!entry) return null;

  const hasFlat = entry.flat_pct != null;
  const hasChannel = entry.per_channel_pct != null;
  const hasButton = entry.per_button_pct != null;
  if (!hasFlat && !hasChannel && !hasButton) return null;

  const t = traits ?? resolveDesignTraits(designType);

  let pct = entry.flat_pct ?? 0;
  if (t.is_tufted) pct += (entry.per_channel_pct ?? 0) * channelsPerSection;
  if (t.has_buttons) pct += (entry.per_button_pct ?? 0) * buttonsPerSection;
  return pct;
}
